import type { Task } from 'fhir/r4';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Form, Select, Input, Message, Icon, Popup, Button } from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';
import type { Option } from 'types';
import type { TaskHelper } from 'utils/Helpers';

import { TASK_ICON_COLORS } from '../../newTaskList/fixtures';
import { TASK_ICON_MAPS } from '../../newTaskList/subList/fixtures';
import type { DefaultOptionType } from '../../taskHeader/types';
import {
  OUTREACH_REASONS_OPTIONS,
  REQUEST_OPTIONS,
  STATUS_OPTIONS,
  STATUS_REASONS_OPTIONS,
  STATUS_COLORS,
  CONSULT_TASK_SUB_TYPE_OPTIONS,
  CONSULT_TOPIC_OPTIONS,
} from '../fixtures';

import {
  DatePickerWrapper,
  FormContainer,
  FormHeader,
  StatusOptionContainer,
  StatusOptionIcon,
  TaskActionsWrapper,
  TaskDescription,
  TaskDescriptionTitle,
  TaskDescriptionWrapper,
  TaskDetailCardTitle,
  TaskDueDateLabel,
  TaskFormGrid,
  TaskIconWrapper,
  TaskType,
} from './taskDetailsForm.styles';

type TaskDetailsFormProps = {
  taskpayload: Task;
  taskHelper: TaskHelper;
  vmURL: string | null;
  audioError: string;
  /**
   * `Task.groupIdentifier.value`, which is `0..1` — `''` for a task with a
   * groupIdentifier carrying no value, and `null` before the task resolves.
   */
  referralId: string | null | undefined;
  orderId: string;
  clipBoard: string;
  practlist: DefaultOptionType[];
  vendorList: Option[];
  referralIdList: string[];
  errorMessage: string;
  isCareplan: boolean;
  consultType: string | null;
  consultTopic: string | null;
  onDateChange: (e: React.SyntheticEvent<HTMLElement> | undefined, semanticprops: { value: Date | null }) => void;
  onAssigneeChange: (e: React.SyntheticEvent<HTMLElement>, dropdownprops: DropdownProps) => void;
  /** Called with `'routine'` or `'asap'` — the two the urgency toggle switches between. */
  onPriorityChange: (newPrio: Task['priority']) => void;
  onReferralIdChange: (referralId: string) => void;
  /**
   * Receives the Dropdown's selected `value`, which is one of `STATUS_OPTIONS` —
   * every entry of which is a FHIR `Task.status` code. The narrowing happens at the
   * `onChange` below, where the widened `DropdownProps['value']` is pinned back.
   */
  onStatusChange: (newStatus: Task['status']) => void;
  onVendorChange: (e: React.SyntheticEvent<HTMLElement>, dropdownprops: DropdownProps) => void;
  /**
   * Semantic's own event type, not intersected with `{ target: HTMLElement }` for the
   * `e.target.textContent` these read: that intersection makes every `onChange`
   * attachment below fail to match. The parent's cast states the element shape.
   */
  onRequestChange: (e: React.SyntheticEvent<HTMLElement>) => void;
  onStatusReasonChange: (e: React.SyntheticEvent<HTMLElement>) => void;
  onCopyToClipboard: (id: string) => void;
  onErrorMessageDismiss: () => void;
  onConsultTypeChange: (consultType: string) => void;
  onConsultTopicChange: (topic: string) => void;
  pendingStatus?: string | null;
  onPendingStatusChange?: (status: string | null) => void;
};

const TaskDetailsForm: React.FC<TaskDetailsFormProps> = ({
  taskpayload,
  referralId,
  orderId,
  clipBoard,
  practlist,
  vendorList,
  referralIdList,
  errorMessage,
  isCareplan,
  consultType,
  consultTopic,
  onDateChange,
  onAssigneeChange,
  onPriorityChange,
  onReferralIdChange,
  onStatusChange,
  onVendorChange,
  onRequestChange,
  onStatusReasonChange,
  onCopyToClipboard,
  onErrorMessageDismiss,
  onConsultTypeChange,
  onConsultTopicChange,
  taskHelper,
  vmURL,
  audioError,
  pendingStatus,
  onPendingStatusChange,
}) => {
  // Track selected status to show statusReason field when needed
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Reset selectedStatus when task status actually changes to cancelled (not just when selected)
  useEffect(() => {
    if (taskpayload?.status === 'cancelled') {
      setSelectedStatus(null);
    }
  }, [taskpayload?.status]);

  useEffect(() => {
    // New task opened → reset form-local state
    setSelectedStatus(null);
    onPendingStatusChange?.(null);
  }, [taskpayload?.id]);

  if (!taskpayload) return null;

  const coding = taskpayload?.code?.coding?.[0];
  const priority = taskpayload?.priority || 'routine';
  const isUrgent = priority === 'asap';
  const isFoodDelivery = coding?.code === 'food-delivery';
  const isTriage = coding?.code === 'triage';
  const isStatusHalt =
    taskpayload?.status === 'cancelled' ||
    taskpayload?.status === 'on-hold' ||
    taskpayload?.status === ('paused' as Task['status']);
  const isStatusReceived = taskpayload?.status === 'received';
  const isConsultationTask = coding?.code === 'consultation';
  const isEnrollmentAction = coding?.code === 'enrollment-action';
  const consultTaskSubType = consultType || '1:1';
  // Only require status reason for enrollment-action tasks when cancelling
  const isCancellingEnrollmentAction =
    (pendingStatus === 'cancelled' || selectedStatus === 'cancelled') && isEnrollmentAction;
  const shouldShowStatusReason = isStatusHalt || isCancellingEnrollmentAction;

  const _getStatusColor = (status: string) => {
    return STATUS_COLORS[status] || 'grey';
  };

  return (
    <FormContainer>
      <FormHeader>
        <TaskIconWrapper>
          {coding?.code && (
            <Icon size="large" name={TASK_ICON_MAPS[coding.code]!} color={TASK_ICON_COLORS[coding.code]!} />
          )}
          {coding?.display && <TaskDetailCardTitle>{coding.display}</TaskDetailCardTitle>}
        </TaskIconWrapper>
        <TaskActionsWrapper>
          {taskHelper.isVMTask() && vmURL ? (
            <Popup trigger={<Icon name="file audio" size="large" color="green" />} on="click">
              {vmURL && <ReactAudioPlayer src={vmURL} autoPlay controls />}
              {audioError && <Message content={audioError} error />}
            </Popup>
          ) : (
            taskHelper.isVMTask()
          )}
          <Popup
            content={isUrgent ? 'HIGH PRIORITY' : 'STANDARD'}
            position="top center"
            trigger={
              <Button
                style={{ border: '1px solid #E0E1E2' }}
                icon
                inverted
                onClick={() => onPriorityChange(isUrgent ? 'routine' : 'asap')}
              >
                <Icon color={isUrgent ? 'yellow' : 'grey'} name="lightning" />
              </Button>
            }
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Form.Field
              name="status"
              data-testid="status"
              selectOnBlur={false}
              control={Select}
              key="status"
              placeholder="CANCELLED"
              value={taskpayload.status}
              options={STATUS_OPTIONS.map((option) => ({
                ...option,
                text: (
                  <StatusOptionContainer>
                    {option.icon && <StatusOptionIcon src={option.icon} alt={option.text} />}
                    <span>{option.text}</span>
                  </StatusOptionContainer>
                ),
              }))}
              onChange={(e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
                e.preventDefault();
                // `DropdownProps['value']` is widened to string | number | bool | array;
                // every option this Dropdown renders comes from STATUS_OPTIONS, whose
                // `value`s are pinned to `Task['status']` by the `satisfies` on the fixture.
                const status = value as Task['status'];
                // Only track pending cancelled status for enrollment-action tasks
                if (status === 'cancelled' && taskpayload.status !== 'cancelled' && isEnrollmentAction) {
                  setSelectedStatus('cancelled');
                  onPendingStatusChange?.(status);
                } else if (status !== 'cancelled') {
                  setSelectedStatus(null);
                  onPendingStatusChange?.(null);
                }
                onStatusChange(status);
              }}
              style={{ minWidth: '180px' }}
            />
          </div>
        </TaskActionsWrapper>
      </FormHeader>
      <TaskDescriptionWrapper>
        <TaskType>{taskpayload?.reasonCode?.text?.split('|')[0] ?? ''}</TaskType>
        <TaskDescriptionTitle>Description</TaskDescriptionTitle>
        <TaskDescription>{taskpayload?.description}</TaskDescription>
      </TaskDescriptionWrapper>
      <Form>
        <TaskFormGrid>
          <Form.Field
            name="owner"
            label="Owner:"
            selectOnBlur={false}
            control={Select}
            search
            key="owner"
            placeholder="Care Team"
            value={taskpayload.owner ? taskpayload.owner.reference?.split('/')[1] : null}
            options={practlist}
            onChange={onAssigneeChange}
            disabled={isCareplan} //block user to update the owner of careplan tasks
          />
          <DatePickerWrapper>
            <TaskDueDateLabel>Due Date:</TaskDueDateLabel>
            <Form.Field
              style={{ margin: '0em 0em 1em' }}
              control={SemanticDatepicker}
              default
              // Show end date if it exists, otherwise show start date
              placeholder={
                taskpayload.executionPeriod &&
                moment(taskpayload.executionPeriod.end ?? taskpayload.executionPeriod.start)
                  .local(true)
                  .format('YYYY-MM-DD')
                  .toString()
              }
              onChange={(e: React.SyntheticEvent<HTMLElement> | undefined, data: { value: Date | null }) => onDateChange(e, data)}
              clearable={false}
              // Show end date if it exists, otherwise show start date
              value={
                taskpayload.executionPeriod &&
                moment(taskpayload.executionPeriod.end ?? taskpayload.executionPeriod.start)
                  .local(true)
                  .toDate()
              }
              size="small"
            />
          </DatePickerWrapper>
          <Form.Input
            name="referralId"
            label="Referral ID:"
            value={referralId}
            type="text"
            placeholder="Enter Referral ID"
            selectOnBlur={false}
            control={Select}
            options={referralIdList.map((k) => {
              return { key: k, value: k, text: k.toUpperCase() };
            })}
            onChange={(e, { value }) => onReferralIdChange(value)}
          />
          {isFoodDelivery && (
            <Form.Field
              name="vendor"
              label="Vendor:"
              control={Select}
              search
              selectOnBlur={false}
              key="vendor"
              placeholder="Not assigned"
              value={taskpayload.restriction ? taskpayload.restriction.recipient![0]?.reference?.split('/')[1] : null}
              options={vendorList}
              onChange={onVendorChange}
            />
          )}
          {isFoodDelivery && (
            <Form.Field
              style={{ color: 'red' }}
              name="order"
              label="Order ID:"
              key="order"
              placeholder="Not assigned"
              control={Input}
              readOnly
              value={orderId}
              icon={'orderId' + orderId === clipBoard ? 'clipboard check' : 'clipboard'}
              id={'orderId' + orderId}
              onClick={() => {
                // Kept as an expression statement: an `if` would emit different JavaScript.
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                orderId && onCopyToClipboard('orderId' + orderId);
              }}
              tooltip="Copy to clipboard"
              popup
            />
          )}
          {isConsultationTask && (
            <Form.Input
              name="consultType"
              label="Consult Type:"
              value={consultTaskSubType}
              type="text"
              placeholder="Enter Consult Type"
              selectOnBlur={false}
              control={Select}
              options={CONSULT_TASK_SUB_TYPE_OPTIONS}
              onChange={(e, { value }) => onConsultTypeChange(value)}
            />
          )}
          {isConsultationTask && consultTaskSubType === 'group' && (
            <Form.Input
              name="consultTopic"
              label="Consult Topic:"
              value={consultTopic || ''}
              type="text"
              placeholder="Select Consult Topic"
              selectOnBlur={false}
              control={Select}
              options={CONSULT_TOPIC_OPTIONS}
              onChange={(e, { value }) => onConsultTopicChange(value)}
            />
          )}
          {isTriage && (
            <Form.Field
              name="request"
              label="Request:"
              selectOnBlur={false}
              control={Select}
              search
              key="request"
              placeholeder="No requests"
              value={taskpayload.businessStatus && taskpayload.businessStatus.text}
              options={REQUEST_OPTIONS}
              onChange={onRequestChange}
            />
          )}
          {shouldShowStatusReason && (
            <Form.Field
              name="statusReason"
              label="Status Reason:"
              selectOnBlur={false}
              control={Select}
              search
              key="statusReason"
              placeholder="Select Reason"
              value={taskpayload.statusReason && taskpayload.statusReason.text}
              options={STATUS_REASONS_OPTIONS}
              onChange={(e: React.SyntheticEvent<HTMLElement>) => {
                // Clear selectedStatus when statusReason is selected
                if (selectedStatus === 'cancelled') {
                  setSelectedStatus(null);
                }
                onStatusReasonChange(e);
              }}
              required={isCancellingEnrollmentAction}
              error={isCancellingEnrollmentAction && !taskpayload?.statusReason?.text}
            />
          )}
          {isStatusReceived && (
            <Form.Field
              name="outreachReason"
              label="Outreach Reason:"
              data-testid="outreachReason"
              selectOnBlur={false}
              control={Select}
              search
              key="statusReason"
              placeholder="SelectReason"
              value={taskpayload.statusReason && taskpayload.statusReason.text}
              options={OUTREACH_REASONS_OPTIONS}
              onChange={onStatusReasonChange}
            />
          )}
        </TaskFormGrid>
        {errorMessage && <Message size="small" content={errorMessage} negative onDismiss={onErrorMessageDismiss} />}
      </Form>
    </FormContainer>
  );
};

export default TaskDetailsForm;
